import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  Icon,
  Stack,
  useColorModeValue,
  useDisclosure,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Badge,
} from '@chakra-ui/react';
import { MdRefresh } from 'react-icons/md';
import { useTranslation } from 'i18n/TranslationProvider';
import { useFirebaseAuth } from 'domains/auth/hooks/useFirebaseAuth';
import { getUserByFirebaseUid } from 'api/api';

import useFetchTrucks from 'domains/truck/hooks/useFetchUserTrucks';
import useDeleteTruckToUser from 'domains/truck/hooks/useDeleteTruckFromUser';
import useUpdateTruck from 'domains/truck/hooks/useUpdateUserTruck';
import useAddTruck from 'domains/truck/hooks/useAddTruckToUser';
import AddEditTruckModal from 'domains/truck/components/AddEditTruckModal.jsx';

import AddEditDriverModal from 'domains/truck/components/AddEditDriverModal';
import useFetchDrivers from 'domains/truck/hooks/useFetchUserDrivers';
import useDeleteDriverFromUser from 'domains/truck/hooks/useDeleteDriverFromUser';
import Trucks from './trucksTab';
import Drivers from './driversTab';
import Trailers from './trailersTab';
import useUpdateDriver from 'domains/truck/hooks/useUpdateUserDriver';
import useAddDriver from 'domains/truck/hooks/useAddDriverToUser';

import useFetchUserTrailers from 'domains/trailer/hooks/useFetchUserTrailers';
import useDeleteTrailerFromUser from 'domains/trailer/hooks/useDeleteTrailerFromUser';
import useAddTrailerToUser from 'domains/trailer/hooks/useAddTrailerToUser';
import useUpdateUserTrailer from 'domains/trailer/hooks/useUpdateUserTrailer';
import AddEditTrailerModal from 'domains/trailer/components/AddEditTrailerModal';
import PageShell from 'components/layout/PageShell';
import { useUnifiedTabsStyles } from 'components/layout/useUnifiedUiStyles';

export default function FleetScreen() {
  const { t } = useTranslation();
  const toast = useToast();
  const cancelRef = useRef();
  const panelBg = useColorModeValue(
    'rgba(255, 255, 255, 0.90)',
    'rgba(15, 28, 63, 0.72)',
  );
  const panelBorderColor = useColorModeValue(
    'rgba(255,255,255,0.95)',
    'rgba(255,255,255,0.16)',
  );
  const { tabListProps, tabProps } = useUnifiedTabsStyles();

  // TRUCK LOGIC
  const {
    data: trucks,
    loading: loadingTrucks,
    error: errorTrucks,
    fetchTrucks,
  } = useFetchTrucks();
  const { deleteTruck, success: deleteSuccessTruck } = useDeleteTruckToUser();
  const { updateTruck, success: updateSuccessTruck } = useUpdateTruck();
  const { addTruck, success: addSuccessTruck } = useAddTruck();

  // DRIVER LOGIC
  const { deleteDriver, success: deleteSuccessDriver } =
    useDeleteDriverFromUser();
  const { updateDriver, success: updateSuccessDriver } = useUpdateDriver();
  const { addDriver, success: addSuccessDriver } = useAddDriver();
  const {
    data: drivers,
    loading: loadingDrivers,
    error: errorDrivers,
    fetchDrivers,
  } = useFetchDrivers();

  // TRAILER LOGIC
  const { deleteTrailer, success: deleteSuccessTrailer } =
    useDeleteTrailerFromUser();
  const { updateTrailer, success: updateSuccessTrailer } =
    useUpdateUserTrailer();
  const { addTrailer, success: addSuccessTrailer } = useAddTrailerToUser();
  const {
    data: trailers,
    loading: loadingTrailers,
    error: errorTrailers,
    fetchTrailers,
  } = useFetchUserTrailers();

  // MODALS & ALERTS
  const {
    isOpen: isTruckModalOpen,
    onOpen: onTruckModalOpen,
    onClose: onTruckModalClose,
  } = useDisclosure();
  const {
    isOpen: isDriverModalOpen,
    onOpen: onDriverModalOpen,
    onClose: onDriverModalClose,
  } = useDisclosure();
  const {
    isOpen: isTrailerModalOpen,
    onOpen: onTrailerModalOpen,
    onClose: onTrailerModalClose,
  } = useDisclosure();
  const {
    isOpen: isTruckDeleteAlertOpen,
    onOpen: onTruckDeleteAlertOpen,
    onClose: onTruckDeleteAlertClose,
  } = useDisclosure();
  const {
    isOpen: isDriverDeleteAlertOpen,
    onOpen: onDriverDeleteAlertOpen,
    onClose: onDriverDeleteAlertClose,
  } = useDisclosure();
  const {
    isOpen: isTrailerDeleteAlertOpen,
    onOpen: onTrailerDeleteAlertOpen,
    onClose: onTrailerDeleteAlertClose,
  } = useDisclosure();

  // SELECTED ITEMS
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [truckToDelete, setTruckToDelete] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [driverToDelete, setDriverToDelete] = useState(null);
  const [selectedTrailer, setSelectedTrailer] = useState(null);
  const [trailerToDelete, setTrailerToDelete] = useState(null);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [isRefreshingFleet, setIsRefreshingFleet] = useState(false);

  // SEARCH AND SCORE
  const [searchQuery, setSearchQuery] = useState('');
  const { uid } = useFirebaseAuth();
  const [partnerScore, setPartnerScore] = useState(null);

  useEffect(() => {
    if (uid) {
      getUserByFirebaseUid(uid).then(u => {
        if (u && u.partnerScore !== undefined) setPartnerScore(u.partnerScore);
      }).catch(e => console.error(e));
    }
  }, [uid]);

  // ADD / EDIT / DELETE HANDLERS - TRUCK
  const handleAddTruck = () => {
    setSelectedTruck(null);
    onTruckModalOpen();
  };
  const handleEditTruck = (truck) => {
    setSelectedTruck(truck);
    onTruckModalOpen();
  };
  const handleDeleteTruck = (truckId) => {
    setTruckToDelete(truckId);
    onTruckDeleteAlertOpen();
  };
  const confirmDeleteTruck = async () => {
    await deleteTruck(truckToDelete);
    await fetchTrucks();
    onTruckDeleteAlertClose();
  };
  const handleSaveTruck = async (truckData) => {
    let savedTruck = null;

    if (truckData?._id) {
      savedTruck = await updateTruck(truckData._id, truckData);
    } else if (selectedTruck?._id) {
      savedTruck = await updateTruck(selectedTruck._id, truckData);
    } else {
      savedTruck = await addTruck(truckData);
    }

    await fetchTrucks();
    return savedTruck;
  };

  // ADD / EDIT / DELETE HANDLERS - DRIVER
  const handleAddDriver = () => {
    setSelectedDriver(null);
    onDriverModalOpen();
  };
  const handleEditDriver = (driver) => {
    setSelectedDriver(driver);
    onDriverModalOpen();
  };
  const handleDeleteDriver = (driverId) => {
    setDriverToDelete(driverId);
    onDriverDeleteAlertOpen();
  };
  const confirmDeleteDriver = async () => {
    await deleteDriver(driverToDelete);
    await fetchDrivers();
    onDriverDeleteAlertClose();
  };
  const handleSaveDriver = async (driverData) => {
    if (selectedDriver) {
      await updateDriver(selectedDriver._id, driverData);
    } else {
      await addDriver(driverData);
    }
    await fetchDrivers();
    onDriverModalClose();
  };

  // ADD / EDIT / DELETE HANDLERS - TRAILER
  const handleAddTrailer = () => {
    setSelectedTrailer(null);
    onTrailerModalOpen();
  };
  const handleEditTrailer = (trailer) => {
    setSelectedTrailer(trailer);
    onTrailerModalOpen();
  };
  const handleDeleteTrailer = (trailerId) => {
    setTrailerToDelete(trailerId);
    onTrailerDeleteAlertOpen();
  };
  const confirmDeleteTrailer = async () => {
    await deleteTrailer(trailerToDelete);
    await fetchTrailers();
    onTrailerDeleteAlertClose();
  };
  const handleSaveTrailer = async (trailerData) => {
    if (selectedTrailer) {
      await updateTrailer(selectedTrailer._id, trailerData);
    } else {
      await addTrailer(trailerData);
    }
    await fetchTrailers();
    onTrailerModalClose();
  };

  // FILTERED LISTS
  const filteredTrucks = Array.isArray(trucks)
    ? trucks.filter((truck) => {
        const q = searchQuery.toLowerCase();
        return truck.licensePlate?.toLowerCase().includes(q);
      })
    : [];
  const filteredDrivers = Array.isArray(drivers)
    ? drivers.filter((driver) => {
        const q = searchQuery.toLowerCase();
        return (
          driver.firstName?.toLowerCase().includes(q) ||
          driver.lastName?.toLowerCase().includes(q) ||
          driver.phone?.toLowerCase().includes(q) ||
          driver.email?.toLowerCase().includes(q)
        );
      })
    : [];

  const filteredTrailers = Array.isArray(trailers)
    ? trailers.filter((trailer) => {
        const q = searchQuery.toLowerCase();
        return trailer.licensePlate?.toLowerCase().includes(q);
      })
    : [];

  const handleRefreshFleet = async () => {
    setIsRefreshingFleet(true);
    try {
      if (activeTabIndex === 0) {
        await fetchTrucks();
      } else if (activeTabIndex === 1) {
        await fetchDrivers();
      } else {
        await fetchTrailers();
      }
      toast({
        title: t('Fleet data refreshed'),
        status: 'success',
        duration: 2200,
        isClosable: true,
      });
    } finally {
      setIsRefreshingFleet(false);
    }
  };

  const cmrSummary = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const warningThreshold = new Date(today);
    warningThreshold.setDate(warningThreshold.getDate() + 14);

    const list = Array.isArray(trucks) ? trucks : [];
    const expired = [];
    const expiringSoon = [];
    const missingDate = [];

    list.forEach((truck) => {
      if (!truck?.cmrInsuranceExpiryDate) {
        missingDate.push(truck);
        return;
      }
      const expiryDate = new Date(truck.cmrInsuranceExpiryDate);
      expiryDate.setHours(0, 0, 0, 0);
      if (expiryDate < today) {
        expired.push(truck);
      } else if (expiryDate <= warningThreshold) {
        expiringSoon.push(truck);
      }
    });

    return { expired, expiringSoon, missingDate };
  }, [trucks]);

  // TOASTS - TRUCK
  useEffect(() => {
    if (updateSuccessTruck) {
      toast({
        title: t('Truck updated.'),
        description: t('The truck has been successfully updated.'),
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    }
  }, [updateSuccessTruck, toast, t]);

  useEffect(() => {
    if (addSuccessTruck) {
      toast({
        title: t('Truck added.'),
        description: t('The new truck has been successfully added.'),
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    }
  }, [addSuccessTruck, toast, t]);

  useEffect(() => {
    if (deleteSuccessTruck) {
      toast({
        title: t('Truck deleted.'),
        description: t('The truck has been successfully deleted.'),
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    }
  }, [deleteSuccessTruck, toast, t]);

  // TOASTS – DRIVER
  useEffect(() => {
    if (updateSuccessDriver) {
      toast({
        title: t('Driver updated.'),
        description: t('The driver has been successfully updated.'),
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    }
  }, [updateSuccessDriver, toast, t]);

  useEffect(() => {
    if (addSuccessDriver) {
      toast({
        title: t('Driver added.'),
        description: t('The new driver has been successfully added.'),
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    }
  }, [addSuccessDriver, toast, t]);

  useEffect(() => {
    if (deleteSuccessDriver) {
      toast({
        title: t('Driver deleted.'),
        description: t('The driver has been successfully deleted.'),
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    }
  }, [deleteSuccessDriver, toast, t]);

  // TOASTS - TRAILER
  useEffect(() => {
    if (updateSuccessTrailer) {
      toast({
        title: t('Trailer updated.'),
        description: t('The trailer has been successfully updated.'),
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    }
  }, [updateSuccessTrailer, toast, t]);

  useEffect(() => {
    if (addSuccessTrailer) {
      toast({
        title: t('Trailer added.'),
        description: t('The new trailer has been successfully added.'),
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    }
  }, [addSuccessTrailer, toast, t]);

  useEffect(() => {
    if (deleteSuccessTrailer) {
      toast({
        title: t('Trailer deleted.'),
        description: t('The trailer has been successfully deleted.'),
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    }
  }, [deleteSuccessTrailer, toast, t]);

  return (
    <PageShell>
      <Stack spacing={6}>

        <Box
          bg={panelBg}
          border="1px solid"
          borderColor={panelBorderColor}
          borderRadius={{ base: '20px', md: '24px' }}
          backdropFilter="blur(10px)"
          boxShadow="0 24px 60px rgba(9, 30, 66, 0.16)"
          px={{ base: 4, md: 6 }}
          py={{ base: 4, md: 6 }}
        >
          <Flex justify="space-between" align="center" mb={4} gap={3} flexWrap="wrap">
            {partnerScore !== null && (
              <Badge colorScheme="blue" borderRadius="full" px={3} py={1}>
                {t('Partner score')}: {Math.max(0, Math.round(Number(partnerScore) || 0))}%
              </Badge>
            )}
            <Button
              size="sm"
              variant="outline"
              leftIcon={<Icon as={MdRefresh} />}
              onClick={handleRefreshFleet}
              isLoading={isRefreshingFleet}
            >
              {t('Refresh')}
            </Button>
          </Flex>

          <Tabs
            variant="unstyled"
            colorScheme="blue"
            index={activeTabIndex}
            onChange={setActiveTabIndex}
          >
            <TabList
              {...tabListProps}
              mb={6}
              display="grid"
              gridTemplateColumns="repeat(3, minmax(0, 1fr))"
            >
              <Tab
                {...tabProps}
                fontSize={{ base: 'sm', md: 'md' }}
              >
                {t('Trucks')}
              </Tab>
              <Tab
                {...tabProps}
                fontSize={{ base: 'sm', md: 'md' }}
              >
                {t('Drivers')}
              </Tab>
              <Tab
                {...tabProps}
                fontSize={{ base: 'sm', md: 'md' }}
              >
                {t('Trailers')}
              </Tab>
            </TabList>

            <TabPanels>
              <TabPanel p={0}>
                <Trucks
                  loading={loadingTrucks}
                  error={errorTrucks}
                  cmrSummary={cmrSummary}
                  setSearchQuery={setSearchQuery}
                  handleAdd={handleAddTruck}
                  filteredTrucks={filteredTrucks}
                  handleEdit={handleEditTruck}
                  handleDelete={handleDeleteTruck}
                  searchQuery={searchQuery}
                />
              </TabPanel>

              <TabPanel p={0}>
                <Drivers
                  loading={loadingDrivers}
                  error={errorDrivers}
                  setSearchQuery={setSearchQuery}
                  handleAdd={handleAddDriver}
                  filteredDrivers={filteredDrivers}
                  handleEdit={handleEditDriver}
                  handleDelete={handleDeleteDriver}
                  searchQuery={searchQuery}
                />
              </TabPanel>

              <TabPanel p={0}>
                <Trailers
                  loading={loadingTrailers}
                  error={errorTrailers}
                  setSearchQuery={setSearchQuery}
                  handleAdd={handleAddTrailer}
                  filteredTrailers={filteredTrailers}
                  handleEdit={handleEditTrailer}
                  handleDelete={handleDeleteTrailer}
                  searchQuery={searchQuery}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      </Stack>

      {/* Add / Edit Modals */}
      <AddEditTruckModal
        isOpen={isTruckModalOpen}
        onClose={onTruckModalClose}
        onSave={handleSaveTruck}
        truck={selectedTruck}
      />
      <AddEditDriverModal
        isOpen={isDriverModalOpen}
        onClose={onDriverModalClose}
        onSave={handleSaveDriver}
        driver={selectedDriver}
      />
      <AddEditTrailerModal
        isOpen={isTrailerModalOpen}
        onClose={onTrailerModalClose}
        onSave={handleSaveTrailer}
        trailer={selectedTrailer}
      />

      {/* Delete Alerts */}
      {/* Truck */}
      <AlertDialog
        isOpen={isTruckDeleteAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={onTruckDeleteAlertClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="18px">
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('Delete Truck')}
            </AlertDialogHeader>
            <AlertDialogBody>
              {t("Are you sure? You can't undo this action afterwards.")}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onTruckDeleteAlertClose}>
                {t('Cancel')}
              </Button>
              <Button colorScheme="red" onClick={confirmDeleteTruck} ml={3}>
                {t('Delete')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Driver */}
      <AlertDialog
        isOpen={isDriverDeleteAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDriverDeleteAlertClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="18px">
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('Delete Driver')}
            </AlertDialogHeader>
            <AlertDialogBody>
              {t("Are you sure? You can't undo this action afterwards.")}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDriverDeleteAlertClose}>
                {t('Cancel')}
              </Button>
              <Button colorScheme="red" onClick={confirmDeleteDriver} ml={3}>
                {t('Delete')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Trailer */}
      <AlertDialog
        isOpen={isTrailerDeleteAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={onTrailerDeleteAlertClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="18px">
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('Delete Trailer')}
            </AlertDialogHeader>
            <AlertDialogBody>
              {t("Are you sure? You can't undo this action afterwards.")}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onTrailerDeleteAlertClose}>
                {t('Cancel')}
              </Button>
              <Button colorScheme="red" onClick={confirmDeleteTrailer} ml={3}>
                {t('Delete')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </PageShell>
  );
}
